import { useTheme } from '@mui/material';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';

import * as Styled from './WorkerTab.styles';
import { WorkerEvent, reconnectMarkers } from './worker.helpers';

export type ReconnectTimelineProps = {
  events: WorkerEvent[];
  from: number;
  to: number;
  height?: number;
};

const VIEWBOX_WIDTH = 400;
const PADDING = 12;

/**
 * Connection events on a horizontal 24 h axis.
 *
 * Drawn as inline SVG rather than with x-charts: the data is a handful of instants, not a series,
 * and a scatter plot would need an invented y value. Events outside the window are dropped by
 * `reconnectMarkers` rather than clamped to an edge.
 */
export const ReconnectTimeline = ({ events, from, to, height = 140 }: ReconnectTimelineProps): JSX.Element => {
  const { formatMessage } = useLocale();
  const { palette } = useTheme();

  const markers = reconnectMarkers(events, from, to);
  const axisY = height / 2;
  const usableWidth = VIEWBOX_WIDTH - 2 * PADDING;

  const colorFor = (tone: string): string => {
    if (tone === 'ok') {
      return palette.chart.ok;
    }

    if (tone === 'warn') {
      return palette.chart.warn;
    }

    if (tone === 'bad') {
      return palette.chart.bad;
    }

    return palette.chart.neutral;
  };

  return (
    <Styled.TimelineWrapper data-testid="dod-worker-reconnect-timeline">
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${height}`}
        role="img"
        aria-label={formatMessage({ id: AppMessages['dod.worker.panel.reconnects'] })}
      >
        <line
          x1={PADDING}
          y1={axisY}
          x2={VIEWBOX_WIDTH - PADDING}
          y2={axisY}
          stroke={palette.chart.grid}
          strokeWidth={2}
        />

        {markers.map((marker) => (
          <circle
            key={marker.id}
            cx={PADDING + marker.position * usableWidth}
            cy={axisY}
            r={6}
            fill={colorFor(marker.tone)}
            data-testid={`dod-worker-marker-${marker.tone}`}
          >
            <title>{`${new Date(marker.at).toLocaleString()} · ${marker.event}${
              marker.detail ? ` · ${marker.detail}` : ''
            }`}</title>
          </circle>
        ))}

        <text x={PADDING} y={height - 8} fontSize={10} fill={palette.info.main}>
          {formatMessage({ id: AppMessages['dod.worker.timelineStart'] })}
        </text>
        <text x={VIEWBOX_WIDTH - PADDING} y={height - 8} fontSize={10} fill={palette.info.main} textAnchor="end">
          {formatMessage({ id: AppMessages['dod.worker.timelineNow'] })}
        </text>
      </svg>
    </Styled.TimelineWrapper>
  );
};
